"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import Navbar from "@/components/Navbar";
import BottomCta from "@/components/BottomCta";
import { FileText, Globe, ShieldCheck, Zap, Users, CheckCircle2, Briefcase, Award } from "lucide-react";
import Footer from "@/components/Footer";

const SERVICES = [
  { icon: Globe, title: "Country Selection Guidance", desc: "We help you identify the best European country based on your profile, goals, and eligibility." },
  { icon: FileText, title: "Documentation Preparation", desc: "Complete handling of all required documents, attestations, translations, and legal paperwork." },
  { icon: ShieldCheck, title: "Application Management", desc: "End-to-end preparation and submission of your work permit application to the right authorities." },
  { icon: Zap, title: "Fast-Track Processing", desc: "Priority coordination with embassies and immigration offices to reduce processing time." },
  { icon: Briefcase, title: "Job Offer Assistance", desc: "Guidance on connecting with verified European employers and structuring your employment offer." },
  { icon: Award, title: "Visa & Residency Activation", desc: "Full support from approval to arrival, including residency registration and settlement guidance." },
];

const STEPS = [
  { num: "01", title: "Profile Assessment", desc: "We review your qualifications, work experience, and goals to identify your best European pathway." },
  { num: "02", title: "Document Collection", desc: "We guide you through every required document, from educational certificates to employment records." },
  { num: "03", title: "Application Submission", desc: "We prepare and submit your complete application to the relevant embassy or immigration authority." },
  { num: "04", title: "Approval & Relocation", desc: "Once approved, we assist with your visa issuance, travel preparation, and European arrival." },
];

const COUNTRIES = [
  { name: "Germany", desc: "Skilled worker visa with strong job market and clear residency pathway." },
  { name: "Poland", desc: "Growing economy with fast processing and accessible work permit system." },
  { name: "Romania", desc: "Lower cost of living with expanding job opportunities across sectors." },
  { name: "Serbia", desc: "Flexible permit conditions with easy documentation and processing." },
  { name: "Hungary", desc: "EU-based permit with access to the broader European job market." },
  { name: "Portugal", desc: "D3 Visa for skilled professionals with long-term residency option." },
];

const WHY_CHOOSE = [
  "Proven track record with European work permit applications",
  "Expert guidance for multiple European countries in one place",
  "End-to-end support from assessment to arrival",
  "Fast documentation and embassy coordination",
  "Transparent process with no hidden fees",
];

export default function EuropeWorkPermitPage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">

          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              Europe Work Permit
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            Europe Work Permit <br />
            <span className="font-serif font-normal text-[#B8893A]">& Visit Visa Services</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            Fast, compliant support for Europe work permits and visit visas. Guidance, documentation, and submission handled end-to-end by our immigration specialists.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
          >
            <Link
              href="/contact"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Apply Now
            </Link>
            <a
              href="https://wa.me/971507987470"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-[#B8893A] bg-transparent border-2 border-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#B8893A]/5 hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Talk to Experts
            </a>
          </motion.div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-y border-gray-100">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-2 gap-12 xl:gap-24">
          <div className="flex flex-col justify-center">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Europe</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">A career and life without borders</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              Europe offers access to world-class job markets, strong wages, excellent quality of life, and a clear pathway to long-term residency and citizenship for skilled professionals.
            </p>
          </div>
          <div className="flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-8 md:pt-0 md:pl-12 xl:pl-24">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">What You Get</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Full application support from start to finish</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              From assessing your eligibility to coordinating with employers and embassies, we handle every step of your Europe work permit process professionally and efficiently.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">What We Offer</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Comprehensive Europe visa and permit services</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            {SERVICES.map((item, i) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={i}
                  className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 hover:shadow-md flex flex-col justify-between"
                >
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{item.title}</h3>
                    <p className="text-gray-500 font-light text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COUNTRIES WE COVER */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Countries We Cover</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Top European destinations for work permits</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            {COUNTRIES.map((item, i) => (
              <div
                key={i}
                className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 hover:shadow-md"
              >
                <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                  <Globe className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{item.name}</h3>
                <p className="text-gray-500 font-light text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR PROCESS */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-[#FAFAFA] border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Our Process</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Simple. Clear. Efficient.</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 relative">
            {STEPS.map((step, i) => (
              <div key={i} className="relative group bg-white p-4 rounded-2xl border border-gray-100/80 shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="text-6xl xl:text-7xl font-black text-gray-100 group-hover:text-[#B8893A]/10 transition-colors duration-300 mb-4">
                  {step.num}
                </div>
                <h3 className="text-lg font-bold mb-2 tracking-tight">{step.title}</h3>
                <p className="text-gray-500 font-light text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Choose Us</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Europe immigration experts you can trust</h2>
            <p className="text-gray-500 font-light leading-relaxed">
              We handle every detail of your European work permit application so you can focus on your future. Transparent, professional, and results-driven.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {WHY_CHOOSE.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border border-gray-100 bg-white rounded-xl">
                <CheckCircle2 className="w-4 h-4 text-[#B8893A] shrink-0 mt-0.5" />
                <span className="text-sm font-semibold text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCta
        title="Ready to Work in Europe?"
        subtitle="Get a free eligibility assessment and find out which European country is the right fit for your profile. Our experts are ready to guide you through the entire process."
      />
      <Footer />
    </main>
  );
}
